import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { Constants } from "../utils/constants";

/**
 * Reusable dropdown component
 * @param {Object} props
 * @param {string} props.label - The currently selected option to display
 * @param {Array} props.options - Array of options to display in the dropdown
 * @param {Function} props.onChange - Callback when an option is selected
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.fullWidth] - Whether the dropdown should take full width
 * @param {string} [props.variant] - Button variant (default, outline, solid)
 * @param {Object} [props.style] - Additional inline styles
 */
const HDropdown = ({
  label,
  options,
  onChange,
  className = "",
  fullWidth = false,
  variant = "default",
  style = {},
  placeholder = "Select option",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Handle clicks outside to close the dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Get button styles based on variant
  const getButtonStyles = () => {
    switch (variant) {
      case "solid":
        return "bg-blue-600 text-white hover:bg-blue-700";
      case "outline":
        return "border border-gray-300 text-gray-700 hover:bg-gray-50";
      default:
        return "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50";
    }
  };

  return (
    <div ref={dropdownRef} className={`relative ${fullWidth ? "w-full" : "inline-block"} ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between ${fullWidth ? "w-full" : ""} px-3 py-2 rounded-lg text-sm ${getButtonStyles()}`}
        style={style}
      >
        <span className="truncate">{label || placeholder}</span>
        <ChevronDown className="h-4 w-4 ml-2 flex-shrink-0" />
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-max min-w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {options.map((option, index) => (
            <button
              key={index}
              type="button"
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
              className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 focus:outline-none transition-colors whitespace-nowrap"
              style={
                option === label
                  ? {
                      backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR,
                      color: "white",
                    }
                  : {}
              }
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default HDropdown;
